import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { brandPackSwatches, listBuiltinBrandPacks } from "@/lib/builder/brand-packs";
import { cn } from "@/lib/utils";

import type { BrandPack } from "@shared/types";

export interface BrandPackSelectorProps {
  /** 当前选中的品牌包 */
  value?: BrandPack | null;
  /** 点击"一键应用"时回调（受控组件，由父组件更新 value） */
  onChange: (pack: BrandPack) => void;
  /** 可选：自定义品牌包列表，默认内置 6 包 */
  packs?: BrandPack[];
}

/**
 * 品牌包选择器（ARCHITECTURE_DESIGN.md §5.5）。
 *
 * 纯展示组件：卡片式网格，每张卡片展示色板预览 + 名称 + 一键应用按钮。
 * 不依赖 builder store，由后续任务在 ThemePermissionPanel 中接线。
 */
export function BrandPackSelector({
  value,
  onChange,
  packs = listBuiltinBrandPacks(),
}: BrandPackSelectorProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {packs.map((pack) => {
        const selected = value?.brand_id === pack.brand_id;
        return (
          <Card
            key={pack.brand_id}
            data-selected={selected ? "true" : undefined}
            className={cn(
              "overflow-hidden transition-shadow",
              selected && "border-primary ring-2 ring-primary/30",
            )}
          >
            <div className="flex h-10 overflow-hidden" aria-hidden="true">
              {brandPackSwatches(pack).map((swatch) => (
                <div
                  key={swatch.name}
                  className="flex-1"
                  style={{ backgroundColor: swatch.value }}
                  title={swatch.name}
                />
              ))}
            </div>
            <div className="space-y-2 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium">{pack.name}</span>
                {selected && <Check className="h-4 w-4 shrink-0 text-primary" />}
              </div>
              <Button
                size="sm"
                variant={selected ? "outline" : "default"}
                className="w-full"
                data-brand-id={pack.brand_id}
                aria-label={`应用品牌包 ${pack.name}`}
                onClick={() => onChange(pack)}
              >
                {selected ? "已应用" : "一键应用"}
              </Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
